/* eslint-disable max-lines */
import { isNil } from 'lodash';
import { CloudServiceProvider } from '../../../constants/AppConstants';
import { listenerApiHelper, uploadFile } from '../../helper/HelperFunctions';
import {
  IAllEnvironmentsRespObj,
  ICreateEnvironmentPayload,
  IDownloadArchitectureDiagramPayload,
  IFetchCategoryAndTechDomainResponse,
  IGetEnvironmentsQueryParams,
  IGetPresignedURLResponse,
  IGetPresignedURLToUploadResponse,
  IGetWorkloadQueryParams,
  IUploadArchitecturePayload,
  IWorkloadPatternRespObj,
} from '../../models/BuildDeployModels';
import { IConfigureWorkLoadsPattern } from '../../models/DashboardModels';
import { storage, STORAGE_CONSTANTS } from '../../services/LocalStorage';
import { ILandingZoneErrorResponse } from '../../store/actions/BuildDeployActions';
import {
  IArchitectureCataloguePayload,
  IArchitectureCatalogueResponse,
  ICloudLandingZoneKeysPayload,
  ICloudLandingZoneKeysResponse,
  IConfigureDeployCloudPayload,
  IConfigureDeployCloudThroughConsolePayload,
  IConfigureLandingZoneRequestPayload,
  ICreateYamlPayload,
  IDeleteLandingZonePayload,
  IDeleteWorkLoadPatternPayload,
  IDeploymentLogsResponse,
  IDeploymentsResponse,
  IEnvironmentsDeployed,
  ILandingZoneState,
  IPipelinesAnalyticsResponse,
  IPipelinesCummulativeAnalyticsResponse,
  IUploadPayload,
  IYamlContent,
  IAppStreamResponse,
  IDeployActivityHistory,
  IFormInterface,
  IIndustryYamlPayload,
  IGetKeysRequestBody,
  IIndustryPolicy,
  IIndustryCompliance,
  IIndustryPolicyRequest,
  IGenAiCreatePayload,
  IGenAiCreateResponse,
  IGenAiCreateDownloadPayload,
  IValidateLandingZonePayload,
  IUploadCustomArchitectureFilePayload,
  IUploadCustomArchitectureFileResponse,
  IUploadCustomCloudConfigurationFilePayload,
  IUploadCustomCloudConfigurationFileResponse,
  ICloudBuildConfigVariable,
  ICloudBuildTfVarsPayload,
  IValidateCustomConfigurationPayload,
  IGenerateArchitectureDiagramsPayload,
  IGenerateArchitectureDiagramsResponse,
  IUploadArchitectureDiagramsPayload,
  IUploadArchitectureDiagramsResponse,
  IReviseArchitectureDiagramsPayload,
  IConvertArchitectureDiagramsPayload,
  IConvertArchitectureDiagramsResponse,
} from '../../store/state/BuildDeployState';
import { API_ROUTE } from '../ApiRoute';
import ApiService from '../ApiService';
import ApiServiceBase from '../ApiServiceBase';
import { ServerType, ServiceType } from '../ServiceType.data';
import { IMultipartFormData } from '../MultipartFormData.data';

export class BuildDeployService {
  private readonly service: ApiServiceBase = new ApiService(
    ServiceType.BuildDeploy,
    ServerType.dca,
  );

  private readonly tangraService: ApiServiceBase = new ApiService(
    ServiceType.None,
    ServerType.tangra,
  );

  private readonly tangraDCAService: ApiServiceBase = new ApiService(
    ServiceType.BuildDeploy,
    ServerType.tangra,
  );
  private readonly dcpService: ApiServiceBase = new ApiService(ServiceType.DCP, ServerType.dcp);

  private readonly dcaService: ApiServiceBase = new ApiService(ServiceType.None, ServerType.dca);

  private readonly dcaBackendService: ApiServiceBase = new ApiService(
    ServiceType.None,
    ServerType.dcaBackend,
  );

  private readonly dcaBackendServiceWithServiceType: ApiServiceBase = new ApiService(
    ServiceType.BuildDeploy,
    ServerType.dcaBackend,
  );

  private readonly localService: ApiServiceBase = new ApiService(
    ServiceType.None,
    ServerType.local,
  );

  public getLandingZone(projectId: string): Promise<ILandingZoneState[]> | ILandingZoneState[] {
    const userId = storage.getItem(STORAGE_CONSTANTS.userId);
    let route: string[] = [];
    if (!isNil(userId)) route = [API_ROUTE.LANDING_ZONE.BASE];
    return this.dcaBackendService.get({ route: route, query: { project: projectId } });
  }

  public getLandingZoneDetails(
    project: string,
  ): Promise<ILandingZoneState[]> | ILandingZoneState[] {
    return this.dcaBackendService.get({
      route: [API_ROUTE.LANDING_ZONE.GET_LANDING_ZONE_DETAILS],
      query: { project },
    });
  }

  //TODO : Change the Post Method to Delete with user Id instead
  public destroyLaningZone(payload: IDeleteLandingZonePayload): Promise<string> | string {
    return this.dcaBackendService.postMultipart(
      {
        route: [API_ROUTE.LANDING_ZONE.DESTROY],
      },
      [payload.uploadData.userdata],
    );
  }

  public destroyWorkLoadPattern(payload: IDeleteWorkLoadPatternPayload): Promise<string> | string {
    const route = [
      API_ROUTE.MICROSERVICES.DESTROY,
      payload.landingZoneId,
      payload.workloadPatternId,
    ];
    return this.service.delete({ route: route });
  }

  public checkLandingZoneStatus(landingZoneId: string) {
    const route: string = `${API_ROUTE.LANDING_ZONE_STATUS}${landingZoneId}`;
    return listenerApiHelper(route);
  }

  public checkMicroServicesPatternStatus(microServiceId: string) {
    const route: string = `${API_ROUTE.LANDING_ZONE_STATUS}${microServiceId}`;
    return listenerApiHelper(route);
  }

  public uploadArchitectureFile(url: string, file: File): Promise<Response> {
    return uploadFile(url, file);
  }

  public startUploadFunc(
    payload: IConfigureLandingZoneRequestPayload,
  ): Promise<ILandingZoneState> | ILandingZoneState {
    const uploadData = payload.uploadData as IUploadPayload;
    //if Arch diagram has a separate endpoint, use that apiEndpoint
    return this.dcaBackendService.postMultipart(
      {
        route: payload.apiEndpoint
          ? [payload.apiEndpoint]
          : payload?.providerName === CloudServiceProvider.MULTICLOUD
            ? [API_ROUTE.CREATE_MULTI_CLOUD]
            : [API_ROUTE.LANDING_ZONE.CREATE],
      },
      [uploadData.userdata, uploadData.file],
    );
  }

  public startCustomLandingZoneUpload(
    payload: IConfigureLandingZoneRequestPayload,
  ): Promise<ILandingZoneState> | ILandingZoneState {
    const uploadData = payload.uploadData as IUploadPayload;
    return this.dcaBackendService.postMultipart(
      {
        route: [API_ROUTE.CREATE_CUSTOM_LANDING_ZONE],
      },
      [uploadData.userdata],
    );
  }

  public getPreviewYamlArchitecture(
    payload: IConfigureLandingZoneRequestPayload,
  ): Promise<ILandingZoneState> | ILandingZoneState {
    const uploadData = payload.uploadData as IUploadPayload;
    return this.service.postMultipart(
      {
        route: [API_ROUTE.CREATE_DIAGRAM],
      },
      [uploadData.userdata, uploadData.file],
    );
  }

  public uploadMicroservicesYamlFnc(payload: IConfigureWorkLoadsPattern): Promise<string> | string {
    const uploadData = payload.uploadData as IUploadPayload;
    return this.service.postMultipart({ route: [API_ROUTE.MICROSERVICES.CREATE] }, [
      uploadData.userdata,
      uploadData.file,
    ]);
  }

  public getConfigURL(
    filename: string,
  ): Promise<IGetPresignedURLResponse> | IGetPresignedURLResponse {
    return this.dcaBackendService.get({
      route: [API_ROUTE.S3.PRESIGNED_URL],
      query: { filename },
    });
  }

  //Service to download the image with given name
  public getDownloadURL(data: {
    filename: string;
    givenname: string;
  }): Promise<IGetPresignedURLResponse> | IGetPresignedURLResponse {
    const { filename, givenname } = data;
    return this.dcaBackendService.get({
      route: [API_ROUTE.S3.PRESIGNED_URL],
      query: { filename, givenname },
    });
  }
  public getYamlContent(filename: string): Promise<IYamlContent> | IYamlContent {
    return this.dcaBackendService.get({
      route: [API_ROUTE.YAML.CONTENT],
      query: { filename },
    });
  }

  public getDefaultTemplateYamlContent(
    data: ICloudLandingZoneKeysPayload,
  ): Promise<IYamlContent> | IYamlContent {
    return this.dcaBackendService.post(
      { route: [API_ROUTE.LANDING_ZONE.BASE, API_ROUTE.YAML.BASE] },
      data,
    );
  }
  public getDefaultTemplateYamlContentForCloud(
    data: ICloudLandingZoneKeysPayload,
  ): Promise<IYamlContent> | IYamlContent {
    return this.dcaBackendService.post(
      { route: [API_ROUTE.LANDING_ZONE.CREATE, API_ROUTE.TFVARS.RETURN_TFVARS] },
      data,
    );
  }

  public getIndustryYamlContent(data: IIndustryYamlPayload): Promise<string> | string {
    const { cloudType, industry_assignment_name } = data;
    return this.dcaBackendService.post(
      {
        route: [API_ROUTE.LANDING_ZONE.INDUSTRY, API_ROUTE.YAML.BASE],
        query: { cloudType },
      },
      { industry_assignment_name },
    );
  }

  public getPresignedURLToUpload(
    filename: string,
    payload?: IArchitectureCataloguePayload,
  ): Promise<IGetPresignedURLToUploadResponse> | IGetPresignedURLToUploadResponse {
    return this.dcaBackendService.post(
      {
        route: [API_ROUTE.S3.PRESIGNED_URL],
        query: { filename },
      },
      payload,
    );
  }

  public getLandingZoneError(
    landingZoneId: string,
  ): Promise<ILandingZoneErrorResponse> | ILandingZoneErrorResponse {
    return this.service.get({
      route: [API_ROUTE.LANDING_ZONE.STATUS, landingZoneId],
    });
  }
  public createLandingZoneService(
    payload: ICreateYamlPayload,
  ): Promise<{ body: string }> | { body: string } {
    return this.dcaBackendService.post(
      {
        route: [API_ROUTE.LANDING_ZONE.BASE, API_ROUTE.YAML.BASE],
      },
      payload,
    );
  }
  public getEnvironmentsDeployedService(
    data: string,
  ): Promise<IEnvironmentsDeployed[]> | IEnvironmentsDeployed[] {
    return this.dcaBackendService.get({
      route: [API_ROUTE.ANALYTICS, API_ROUTE.CLOUD.CSP],
      query: { project: data },
    });
  }
  public getLandingZoneDeploymentStatusServive(
    data: string,
  ): Promise<IEnvironmentsDeployed[]> | IEnvironmentsDeployed[] {
    return this.dcaBackendService.get({
      route: [API_ROUTE.ANALYTICS, API_ROUTE.CLOUD.STATUS],
      query: { project: data },
    });
  }
  public getArchitectureListServive():
    | Promise<IArchitectureCatalogueResponse[]>
    | IArchitectureCatalogueResponse[] {
    return this.dcaBackendService.get({
      route: [API_ROUTE.S3.FILES],
    });
  }
  public setArchitectureViewCount(id: string): Promise<string> | string {
    return this.dcaBackendService.patch({ route: [API_ROUTE.S3.FILES] }, { Id: id });
  }
  public setArchitectureDiagram(
    data: IArchitectureCataloguePayload,
  ): Promise<IArchitectureCatalogueResponse> | IArchitectureCatalogueResponse {
    return this.dcaBackendService.post({ route: [API_ROUTE.IMAGE_UPLOAD] }, data);
  }
  public getPipelineAnalyticsService():
    | Promise<IPipelinesAnalyticsResponse[]>
    | IPipelinesAnalyticsResponse[] {
    return this.dcaBackendService.get({
      route: [API_ROUTE.ANALYTICS, API_ROUTE.PIPELINE],
    });
  }
  public getPipelineCummulativeAnalyticsService():
    | Promise<IPipelinesCummulativeAnalyticsResponse[]>
    | IPipelinesCummulativeAnalyticsResponse[] {
    return this.dcaBackendService.get({
      route: [API_ROUTE.ANALYTICS, API_ROUTE.PIPELINE_CUMULATIVE],
    });
  }
  public getCloudLandingZoneKeyservice(
    data: IGetKeysRequestBody,
  ): Promise<ICloudLandingZoneKeysResponse[]> | ICloudLandingZoneKeysResponse[] {
    if (data.iac) {
      return this.dcaBackendService.get({
        route: [API_ROUTE.LANDING_ZONE.CREATE, API_ROUTE.GET_KEYS],
        query: { cloudType: data.csp, iac: data.iac },
      });
    } else {
      return this.dcaBackendService.get({
        route: [API_ROUTE.LANDING_ZONE.CREATE, API_ROUTE.GET_KEYS],
        query: { cloudType: data.csp },
      });
    }
  }
  public configureDeployCloudService(
    data: IConfigureDeployCloudPayload,
  ): Promise<{ instanceId: string }> | { instanceId: string } {
    return this.dcaBackendService.post(
      {
        route: [API_ROUTE.LANDING_ZONE.ALTERNATE, API_ROUTE.WORKFLOW.BASE, API_ROUTE.START],
      },
      data,
    );
  }
  public configureDeployCloudThroughConsoleService(
    data: IConfigureDeployCloudThroughConsolePayload,
  ): Promise<{ status: string }> | { status: string } {
    return this.tangraService.post(
      {
        route: [API_ROUTE.WORKFLOW_CONSOLE_START],
      },
      data,
    );
  }
  public setCloudLandingZoneKeyservice(
    data: ICloudLandingZoneKeysPayload,
  ): Promise<string> | string {
    return this.dcaBackendService.post(
      { route: [API_ROUTE.LANDING_ZONE.BASE, API_ROUTE.YAML.BASE] },
      data,
    );
  }
  public getDeploymentsList(
    projectId: string,
    accountId: string,
  ): Promise<IDeploymentsResponse[]> | IDeploymentsResponse[] {
    let route: string[] = [];
    route = [API_ROUTE.GET_FOUNDATION_HISTORY];
    return this.dcaBackendService.get({ route: route, query: { projectId, accountId } });
  }

  public getWorlFlowLogs(
    landingZoneId: string,
    csp: string,
  ): Promise<IDeploymentsResponse[]> | IDeploymentsResponse[] {
    let route: string[] = [];
    route = [API_ROUTE.DOWNLOAD_WORKFLOW_LOGS];
    return this.dcaBackendService.get({
      route: route,
      query: { LandingZoneId: landingZoneId, csp },
    });
  }

  public cloudLandingZoneDeployStatusService(
    id: string,
  ): Promise<{ state: string }> | { state: string } {
    let route: string[] = [];
    // keeping commented code for later use
    //const landingZoneId: string = 'c6521ec6-9170-496a-a7c2-53015dda012a';
    route = [API_ROUTE.WORKFLOW.STATUS, id];
    return this.dcpService.get({ route: route });
  }

  public getDeploymentAuditList(
    landingZoneId: string,
    workflowId: string,
  ): Promise<IDeploymentLogsResponse[]> | IDeploymentLogsResponse[] {
    let route: string[] = [];
    route = [API_ROUTE.LANDING_ZONE.ALTERNATE, API_ROUTE.WORKFLOW.BASE, landingZoneId, workflowId];
    return this.dcaBackendService.get({ route: route });
  }

  public getCoftaUrl(): Promise<IAppStreamResponse> | IAppStreamResponse {
    return this.tangraService.get({
      route: [API_ROUTE.APP_STREAM],
    });
  }

  public updateLandingZoneDeployLogs(
    status: string,
    landinZoneId: string,
    workflowId: string,
    instanceId: string,
  ): Promise<IDeployActivityHistory> | IDeployActivityHistory {
    return this.dcaBackendService.post(
      {
        route: [API_ROUTE.INSTANCE, landinZoneId, workflowId, instanceId],
        query: { status },
      },
      {},
    );
  }
  public getMultiCloudYamlContent(payload: IFormInterface): Promise<IYamlContent> | IYamlContent {
    return this.dcaBackendService.post(
      {
        route: [API_ROUTE.LANDING_ZONE.MULTI_CLOUD, API_ROUTE.YAML.BASE],
      },
      payload,
    );
  }

  public landingZoneDeployStart(
    payload: IConfigureLandingZoneRequestPayload,
  ): Promise<ILandingZoneState> | ILandingZoneState {
    const uploadData = payload.uploadData as IUploadPayload;
    //if Arch diagram has a separate endpoint, use that apiEndpoint
    return this.service.postMultipart(
      {
        route: payload.apiEndpoint
          ? [payload.apiEndpoint]
          : payload?.providerName === CloudServiceProvider.MULTICLOUD
            ? [API_ROUTE.CREATE_MULTI_CLOUD]
            : [API_ROUTE.LANDING_ZONE.CREATE],
      },
      [uploadData.userdata, uploadData.file],
    );
  }
  public createEnvironment(
    uploadData: ICreateEnvironmentPayload,
  ): Promise<IAllEnvironmentsRespObj> | IAllEnvironmentsRespObj {
    //if Arch diagram has a separate endpoint, use that apiEndpoint
    return this.service.postMultipart(
      {
        route: [API_ROUTE.LANDING_ZONE.CREATE_ENV],
      },
      [{ name: 'userdata', content: JSON.stringify(uploadData.userdata) }, uploadData.file],
    );
  }

  public getWorkloads(
    payload: IGetWorkloadQueryParams,
  ): Promise<Array<IWorkloadPatternRespObj>> | Array<IWorkloadPatternRespObj> {
    return this.dcaBackendService.get({
      route: [API_ROUTE.MODERNIZATION.BASE, API_ROUTE.MODERNIZATION.WORKLOAD_PATTERNS],
      query: { ...payload },
    });
  }

  public getEnvironments(
    payload: IGetEnvironmentsQueryParams,
  ): Promise<Array<IWorkloadPatternRespObj>> | Array<IWorkloadPatternRespObj> {
    return this.dcaBackendService.get({
      route: [API_ROUTE.MODERNIZATION.BASE, API_ROUTE.MODERNIZATION.ENVIRONMENTS],
      query: { ...payload },
    });
  }

  public validateYaml(payload: IUploadPayload): Promise<string> | string {
    return this.service.postMultipart(
      {
        route: [API_ROUTE.YAML.VALIDATE_YAML],
      },
      [payload.userdata, payload.file],
    );
  }

  public validateCustomTemplateZipFile(
    payload: IValidateCustomConfigurationPayload,
  ): Promise<string> | string {
    return this.dcaBackendService.postMultipart(
      { route: [API_ROUTE.VALIDATE_CUSTOM_CONFIGURATION] },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            csp: payload.csp,
            userId: payload.userId,
            iac: payload.iac,
            landingZoneId: payload.landingZoneId,
            branch: payload.branch,
          }),
        },
      ],
    );
  }

  public validateLandingZone(payload: IValidateLandingZonePayload): Promise<string> | string {
    return this.dcaBackendService.get({
      route: [API_ROUTE.VALIDATE_LANDING_ZONE],
      query: { LandingZoneId: payload.landingZoneId, CSP: payload.csp },
    });
  }

  public validateForCloudYaml(payload: IUploadPayload): Promise<string> | string {
    return this.dcaBackendService.postMultipart(
      {
        route: [API_ROUTE.YAML.VALIDATE_CONFIGURATION],
        //route: ['validate-configuration'],
      },
      [payload.userdata, payload.file],
    );
  }

  public validatePollYaml(planId: string): Promise<string> | string {
    return this.service.postMultipart(
      {
        route: [API_ROUTE.YAML.VALIDATE_REPORT],
      },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            planId: planId,
          }),
        },
      ],
    );
  }

  public validateCustomPollYaml(planId: string): Promise<string> | string {
    return this.dcaBackendService.postMultipart(
      {
        route: [API_ROUTE.VALIDATE_CUSTOM_TERRAFORM_PLAN],
      },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            planId: planId,
          }),
        },
      ],
    );
  }

  public validateForCloudPollYaml(planId: string): Promise<string> | string {
    return this.dcaBackendService.postMultipart(
      {
        route: [API_ROUTE.YAML.VALIDATE_TERRAFORM_PLAN],
      },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            planId: planId,
          }),
        },
      ],
    );
  }

  public fetchIndustryCompliances():
    | Promise<Array<IIndustryCompliance>>
    | Array<IIndustryCompliance> {
    return this.dcaBackendServiceWithServiceType.get({
      route: [API_ROUTE.INDUSTRY_COMPLIANCE.COMPLIANCES],
      query: { version: 'v1' },
    });
  }

  public fetchIndustryPolicies(
    payload?: IIndustryPolicyRequest,
  ): Promise<Array<IIndustryPolicy>> | Array<IIndustryPolicy> {
    return this.dcaBackendService.get({
      route: [API_ROUTE.DCA, API_ROUTE.INDUSTRY_COMPLIANCE.STANDARD_POLICIES],
      query: {
        version: 'v1',
        ...(payload ? { 'csp': payload.csp, 'industry': payload.industry } : {}),
      },
    });
  }
  public fetchIndustryPoliciesForCloud(
    payload?: IIndustryPolicyRequest,
  ): Promise<Array<IIndustryPolicy>> | Array<IIndustryPolicy> {
    return this.dcaBackendService.get({
      route: [API_ROUTE.DCA, API_ROUTE.INDUSTRY_COMPLIANCE.POLICIES],
      query: {
        ...(payload ? { 'csp': payload.csp, 'industry': payload.industry } : {}),
      },
    });
  }

  public downloadArchitectureDiagram(
    payload: IDownloadArchitectureDiagramPayload,
  ): Promise<string> | string {
    return this.dcaBackendService.get({
      route: [API_ROUTE.S3.BUCKET, API_ROUTE.ARCHITECTURE.DOWNLOAD],
      query: { id: payload.fileId, type: payload.fileType },
    });
  }

  public fetchCategoryAndTechDomain():
    | Promise<IFetchCategoryAndTechDomainResponse>
    | IFetchCategoryAndTechDomainResponse {
    return this.dcaBackendServiceWithServiceType.get({
      route: [API_ROUTE.GET_CATEGORY_DOMAIN],
    });
  }

  public uploadArchitecture(payload: IUploadArchitecturePayload): Promise<string> | string {
    return this.dcaService.postMultipart(
      { route: [API_ROUTE.S3.BUCKET, API_ROUTE.ARCHITECTURE.UPLOAD] },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            userId: payload.userId,
            techPlatform: payload.techPlatform,
            architectureName: payload.architectureName,
            category: payload.category,
            techDomain: payload.techDomain,
            description: payload.description,
            releventUsecase: payload.releventUsecase,
          }),
        },
      ],
    );
  }

  public fetchIndustryPoliciesYaml(payload: Array<string>):
    | Promise<{
        [key: string]: string;
      }>
    | {
        [key: string]: string;
      } {
    return this.dcaBackendServiceWithServiceType.get({
      route: [API_ROUTE.INDUSTRY_COMPLIANCE.COMPLIANCES, API_ROUTE.YAML.POLICY],
      query: { industryCompliances: `["${payload.join('", "')}"]` },
    });
  }

  public fetchTerraformCode(payload: IUploadPayload): Promise<string> | string {
    return this.service.postMultipart(
      {
        route: [API_ROUTE.DOWNLOAD_TERRAFORM],
      },
      [payload.userdata, payload.file],
    );
  }
  public fetchTerraformCodeForCloud(payload: IUploadPayload): Promise<string> | string {
    return this.dcaBackendService.postMultipart(
      {
        route: [API_ROUTE.DOWNLOAD_TERRAFORM],
      },
      [payload.userdata, payload.file],
    );
  }

  public getGenAiCreateServices(
    data: IGetKeysRequestBody,
  ): Promise<Record<string, Array<string>>> | Record<string, Array<string>> {
    return this.dcaBackendService.get({
      route: [API_ROUTE.GEN_AI_CREATE.SERVICES],
      query: { csp: data.csp },
    });
  }

  public getGenAiCreateYamlContent(
    data: IGenAiCreatePayload,
  ): Promise<IGenAiCreateResponse> | IGenAiCreateResponse {
    return this.dcaBackendService.postMultipart(
      { route: [API_ROUTE.GEN_AI_CREATE.GENERATE_TERRAFORM] },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            'csp': data.csp,
            'ai_model': data.aiModel,
            'services': data.services,
            'landing_zone_name': data.landingZoneName,
          }),
        },
      ],
    );
  }
  public getGenAiCreatePollCompletion(data: IGenAiCreateDownloadPayload): Promise<string> | string {
    return this.service.postMultipart(
      { route: [API_ROUTE.GEN_AI_CREATE.POLL_GENERATION_COMPLETED] },
      [
        {
          name: 'data',
          content: JSON.stringify({
            'services': data.services,
            'terraformFolderName': data.terraformFolderName,
          }),
        },
      ],
    );
  }
  public getGenAiCreateDownload(data: IGenAiCreateDownloadPayload): Promise<string> | string {
    return this.dcaBackendService.postMultipart(
      { route: [API_ROUTE.GEN_AI_CREATE.DOWNLOAD_TERRAFORM] },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            'services': data.services,
            'terraformFolderName': data.terraformFolderName,
          }),
        },
      ],
    );
  }

  public uploadCustomTemplateZipFle(
    data: IUploadCustomCloudConfigurationFilePayload,
  ):
    | Promise<IUploadCustomCloudConfigurationFileResponse>
    | IUploadCustomCloudConfigurationFileResponse {
    return this.dcaBackendService.postMultipart(
      { route: [API_ROUTE.GEN_AI_CREATE.UPLOAD_TERRAFORM] },
      [
        { name: 'zip_file', content: data.zip_file },
        { name: 'branch', content: data.branch },
        { name: 'csp', content: data.csp },
        { name: 'landing_zone_id', content: data.landing_zone_id },
      ],
    );
  }
  public getCloudBuildConfigVariables(
    payload: ICloudBuildTfVarsPayload,
  ): Promise<Array<ICloudBuildConfigVariable>> | Array<ICloudBuildConfigVariable> {
    // Second request: Fetch Terraform code
    return this.dcaBackendService.get({
      route: [API_ROUTE.GET_CONFIG_VARIABLES],
      query: { csp: payload.csp },
    });
  }
  public generateArchitectureDiagram(
    data: IGenerateArchitectureDiagramsPayload,
  ): Promise<IGenerateArchitectureDiagramsResponse> | IGenerateArchitectureDiagramsResponse {
    return this.dcaBackendService.postMultipart(
      { route: [API_ROUTE.GEN_AI.ARCHITECTURE.BASE, API_ROUTE.GEN_AI.ARCHITECTURE.GENERATE] },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            'prompt': data.prompt,
            'diagramType': data.diagramType,
          }),
        },
      ],
    );
  }
  public generateArchitectureDiagramsRevision(
    data: IReviseArchitectureDiagramsPayload,
  ): Promise<IGenerateArchitectureDiagramsResponse> | IGenerateArchitectureDiagramsResponse {
    return this.dcaBackendService.postMultipart(
      {
        route: [
          API_ROUTE.GEN_AI.ARCHITECTURE.BASE,
          API_ROUTE.GEN_AI.ARCHITECTURE.REVISE,
        ],
      },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            'existingDiagram': data.existingDiagram,
            'revision': data.revision,
            'diagramType':data.diagramType,
          }),
        },
      ],
    );
  }
  public generateArchitectureDiagramsConversion(
    data: IConvertArchitectureDiagramsPayload,
  ): Promise<IConvertArchitectureDiagramsResponse> | IConvertArchitectureDiagramsResponse {
    return this.dcaBackendService.postMultipart(
      {
        route: [
          API_ROUTE.GEN_AI.ARCHITECTURE.BASE,
          API_ROUTE.GEN_AI.ARCHITECTURE.CONVERT,
        ],
      },
      [
        {
          name: 'userdata',
          content: JSON.stringify({
            'existingDiagram': data.existingDiagram,
            'diagramType':data.diagramType,
          }),
        },
      ],
    );
  }

  public uploadGeneratedArchitectureDiagram(
    diagramData: IUploadArchitectureDiagramsPayload,
    supportingFile?: File,
  ): Promise<IUploadArchitectureDiagramsResponse> | IUploadArchitectureDiagramsResponse {
    const formParts: IMultipartFormData[] = [
      // JSON metadata
      {
        name: 'userdata',
        content: JSON.stringify(diagramData),
      },
    ];
    if (supportingFile) {
      // Supporting file if provided
      formParts.push({
        name: 'file',
        content: supportingFile,
      });
    }
    return this.dcaBackendService.postMultipart(
      { route: [API_ROUTE.GEN_AI.ARCHITECTURE.BASE, API_ROUTE.GEN_AI.ARCHITECTURE.UPLOAD] },
      formParts,
    );
  }
}

const buildDeployService = new BuildDeployService();
export default buildDeployService;
